import React, { ReactNode } from 'react';
import useLogger from 'hooks/useLogger';
import { extractChunkNameFromError } from 'utility/utility';

type Props = {
  errorMessage?: string;
  componentId?: string;
  fallback?: ReactNode;
  children: ReactNode;
  logError?: (error: Error, componentId?: string) => void;
};

type State = {
  hasError: boolean;
};

type ErrorBoundaryWithLoggerProps = {
  children: React.ReactNode;
  errorMessage?: string;
  componentId: string;
  fallback?: React.ReactNode;
};

class ErrorBoundary extends React.Component<Props, State> {
  // eslint-disable-next-line react/state-in-constructor
  state: State = {
    hasError: false,
  };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Invoke the logError function if provided
    const { logError = () => {}, componentId } = this.props;
    if (logError) {
      logError(error, componentId);
    } else {
      // eslint-disable-next-line no-console
      console.error(componentId, error, errorInfo);
    }
  }

  render() {
    const { hasError } = this.state;
    const { errorMessage = '', children, fallback } = this.props;
    if (hasError) {
      return fallback || errorMessage || null;
    }

    return children;
  }
}

const ErrorBoundaryWithLogger = ({
  errorMessage = '',
  componentId,
  fallback,
  children,
}: ErrorBoundaryWithLoggerProps) => {
  const { error: logError, logChunkLoadError } = useLogger();

  const isChunkLoadError = (error: Error): boolean => {
    return (
      error?.name === 'ChunkLoadError' ||
      error?.message?.includes('Loading chunk') ||
      error?.message?.includes('Loading CSS chunk') ||
      error?.message?.includes('ChunkLoadError')
    );
  };

  const handleLogError = (error: Error, componentIdParam?: string) => {
    if (isChunkLoadError(error)) {
      const chunkName = extractChunkNameFromError(error, componentIdParam);
      logChunkLoadError(
        componentIdParam ?? 'Unknown',
        chunkName,
        error?.message || 'Unknown chunk load error',
        {
          errorType: error?.name || 'ChunkLoadError',
        },
      );
    } else {
      const ErrorMessage = error?.message?.toString() ?? '';
      const ErrorName = error?.name?.toString() ?? '';
      const ErrorStack = error?.stack?.toString() ?? 'No stack trace available';

      logError(`ErrorBoundary caught an error in ${componentIdParam}: ${error?.message}`, {
        ErrorMessage,
        ErrorName,
        ErrorStack,
      });
    }
  };

  return (
    <ErrorBoundary
      errorMessage={errorMessage}
      componentId={componentId}
      logError={handleLogError}
      fallback={fallback}>
      {children}
    </ErrorBoundary>
  );
};

export default ErrorBoundaryWithLogger;
